/**
 * The films and galleries on the case study pages. Everything here is studio
 * work, served from the catalogue at orkhan.art and its media store.
 */
import { MEDIA as STUDIO } from "./content";

const SITE = "https://orkhan.art";
const MEDIA = "https://cms.raisonne.work";

const still = (slug: string, ext: string) => `${SITE}/api/artworkimage/file/${slug}-display.${ext}`;
const clip = (slug: string) => `${MEDIA}/api/artworkvideo/file/${slug}-video.mp4`;
const collection = (slug: string) => `${SITE}/catalog/collections/${slug}`;

export interface Piece {
  title: string;
  /** What the tile shows. */
  poster: string;
  /** Moving work: played over the tile and in the viewer. */
  video?: string;
  /** Still work: the larger picture for the viewer. */
  full?: string;
}

export interface Film {
  src: string;
  poster?: string;
  label: string;
  /** CSS aspect ratio of the frame. */
  ratio: string;
  /** A portrait film: it stands beside the text instead of running the width of the page. */
  upright?: boolean;
}

export interface Shelf {
  id: string;
  label: string;
  title: string;
  /** How many works the series holds; the page shows a few of them. */
  of?: number;
  /** CSS aspect ratio of one tile, and tiles per row. */
  ratio: string;
  cols: number;
  /** The posters already move, so a tile does not load its video under the pointer. */
  animated?: boolean;
  more: string;
  pieces: Piece[];
}

export interface Work {
  film?: Film;
  shelves: Shelf[];
}

/** A moving piece: [title, catalogue slug]. */
const moving = (rows: Array<[string, string]>, ext: string): Piece[] =>
  rows.map(([title, slug]) => ({ title, poster: still(slug, ext), video: clip(slug) }));

const EASTERN = "eastern-promises-preserving-orientalist-painting-through-data-science-4";

export const WORKS: Record<string, Work> = {
  "eastern-promises": {
    film: {
      src: `${SITE}/media/original/user/admin/05b8f5193bf0463f9cad7af9ee0bfb37.eastern-promises-preserving-orientalist-.mp4`,
      poster: still(EASTERN, "png"),
      label: "the panorama",
      ratio: "32 / 9",
    },
    shelves: [
      {
        id: "chapters",
        label: "the chapters",
        title: "one film for each subject in the archive.",
        ratio: "9 / 16",
        cols: 7,
        more: collection("eastern-promises"),
        pieces: moving(
          [
            ["Portraits", "eastern-promises-portraits-5"],
            ["Still Life", "eastern-promises-still-life-6"],
            ["Military and Political Scenes", "eastern-promises-military-and-political-scenes-7"],
            ["Landscapes and Nature", "eastern-promises-landscapes-and-nature-8"],
            ["Harem", "eastern-promises-harem-9"],
            ["Palace Life", "eastern-promises-palace-life-10"],
            ["Architecture and Cultural Monuments", "eastern-promises-architecture-and-cultural-monuments-11"],
          ],
          "png"
        ),
      },
    ],
  },
  muraqqa: {
    shelves: [
      {
        id: "data-miniatures",
        label: "data miniatures",
        title: "the miniatures the model painted back.",
        of: 111,
        ratio: "9 / 16",
        cols: 8,
        animated: true,
        more: collection("muraqqa-dataminiatures"),
        pieces: moving(
          [
            ["Flow of Life #111", "flow-of-life-111-111"],
            ["Heavenly Passage #110", "heavenly-passage-110-110"],
            ["Poetry Reading #108", "poetry-reading-108-108"],
            ["Garden Rendezvous #107", "garden-rendezvous-107-107"],
            ["Novruz #106", "novruz-106-106"],
            ["Sapphire Serenade #105", "sapphire-serenade-105-105"],
            ["The Ark #102", "the-ark-102-102"],
            ["Sunset of the Epoch #100", "sunset-of-the-epoch-100-100"],
          ],
          "gif"
        ),
      },
      {
        id: "neural-impressions",
        label: "neural impressions",
        title: "the same miniatures, set in motion.",
        ratio: "9 / 16",
        cols: 7,
        animated: true,
        more: collection("muraqqa-neuralimpressions"),
        pieces: moving(
          [
            ["Neural Impressions #015", "m-neural-impressions-015-15"],
            ["Neural Impressions #023", "m-neural-impressions-023-23"],
            ["Neural Impressions #028", "m-neural-impressions-028-28"],
            ["Neural Impressions #036", "m-neural-impressions-036-36"],
            ["Neural Impressions #058", "m-neural-impressions-058-58"],
            ["Neural Impressions #062", "m-neural-impressions-062-62"],
            ["Neural Impressions #073", "m-neural-impressions-073-73"],
          ],
          "gif"
        ),
      },
    ],
  },
  visions: {
    film: {
      src: "https://raw2.seadn.io/collection/visions-by-orkhan/image_type_hero_desktop/fb62eb2c9f7f80ec1f7df88d796b0f/cffb62eb2c9f7f80ec1f7df88d796b0f.mp4",
      label: "visions / promo",
      ratio: "16 / 9",
    },
    shelves: [
      {
        id: "visions",
        label: "the visions",
        title: "old masters, as the model remembers them.",
        of: 666,
        ratio: "1 / 1",
        cols: 4,
        more: collection("visions"),
        pieces: [666, 665, 664, 663, 662, 661, 659, 656].map(n => ({
          title: `Visions #${n}`,
          poster: still(`visions-${n}-${n}`, "avif"),
          full: `${SITE}/api/artworkimage/file/visions-${n}-${n}-main.avif`,
        })),
      },
    ],
  },
  "paul-and-shark": {
    film: {
      src: `${MEDIA}/api/video/file/the_a.i._odyssey_-_paul%26shark_x_orkhan%20(2160p).mp4`,
      label: "the a.i. odyssey / paul & shark × orkhan",
      ratio: "16 / 9",
    },
    shelves: [],
  },
  "saving-aesthetics": {
    film: { src: `${STUDIO}/work/clips/clip-02.mp4`, label: "saving aesthetics", ratio: "9 / 16", upright: true },
    shelves: [],
  },
};
